import {myAxios} from "@/services/myAxios";
import {eventBus} from "@/main";
import L from "leaflet";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export function getDepth(coordinates) {
    let URL = 'api/v1/depth?lat=' + coordinates.lat + '&lon=' + coordinates.lng

    myAxios.get(URL).then(resp => {
        L.popup().setLatLng(coordinates).setContent(resp.data + ' m').openOn(this.map);
    })
}

export async function getDepthAsync(coordinates) {
    let URL = 'api/v1/depth?lat=' + coordinates.lat + '&lon=' + coordinates.lng
    const resp = await myAxios.get(URL);
    return resp?.data ?? -1;
}

export function getWeather(coordinates, time) {

    let URL = 'https://nogotiles.bgeo.fi:6011/weather_info?lat=' + coordinates.lat + '&lon=' + coordinates.lng + '&t=' + time

    myAxios.get(URL).then(resp => {
        let temperature = ''
        let wind = ''
        if (!resp.data.temperature.toString().startsWith('1.7976931348'))
            temperature = 'Temperature: ' + resp.data.temperature + '° C'

        if (!resp.data.wind.toString().startsWith('1.7976931348'))
            wind = 'Wind: ' + resp.data.wind + ' m/s (' + resp.data.windAngle + '°)'

        let content = 'Date: ' + dayjs.unix(time).format('DD-MMM HH:mm') + '<br>'
        content += temperature ? temperature + '<br>' : ''
        if (wind)
            content += wind

        L.popup().setLatLng(coordinates).setContent(content).openOn(this.map);
    }).catch(err => {
        console.warn('Fallback weather endpoint unavailable', err);
        let content = 'Date: ' + dayjs.unix(time).format('DD-MMM HH:mm') + '<br>'
        content += 'Weather is not available'
        L.popup().setLatLng(coordinates).setContent(content).openOn(this.map);
    })
}


const METEOBLUE_API_URL = "https://my.meteoblue.com/packages/basic-1h_basic-day_current_sea-1h_sea-day";
const METEOBLUE_API_KEY = "6MonjPXwB4DKEhHa";
const METEOBLUE_DEFAULT_PARAMS = {
    format: "json",
    forecast_days: 7,
    tz: "GMT",
    windspeed: "ms-1",
    timeformat: "iso8601",
    asl: 0
};

export async function getWindyWeather(coordinates, time) {
    try {
        await getWindyWeatherReal(coordinates, time);
    } catch (error) {
        const errorMessage = resolveForecastError(error);
        eventBus.$emit('show-windy-weather-error', {
            coordinates,
            time,
            error: errorMessage
        });
    }
}

export async function getWindyWeatherReal(coordinates, time) {
    try {
        const response = await myAxios.get(METEOBLUE_API_URL, {
            params: buildMeteoblueParams(coordinates)
        });

        if (!response?.data) {
            throw new Error('Empty response from Meteoblue API');
        }

        if (typeof response.data === 'object' && response.data.status === 'error') {
            const details = response.data.error || response.data.message || 'Unknown Meteoblue error';
            throw new Error(details);
        }

        const processedData = processMeteoblueResponse(response.data, {
            requestedUnix: time
        });

        eventBus.$emit('show-windy-weather', {
            coordinates,
            time,
            weatherData: processedData
        });

        return processedData;
    } catch (error) {
        const errorMessage = resolveForecastError(error);
        eventBus.$emit('show-windy-weather-error', {
            coordinates,
            time,
            error: errorMessage
        });
        throw error;
    }
}

function buildMeteoblueParams(coordinates) {
    return {
        ...METEOBLUE_DEFAULT_PARAMS,
        lat: Number(coordinates.lat),
        lon: Number(coordinates.lng),
        apikey: METEOBLUE_API_KEY
    };
}

function resolveForecastError(error) {
    if (error?.response?.status === 401) {
        return 'Meteoblue API key rejected';
    }
    if (error?.response?.status === 429) {
        return 'Meteoblue rate limit exceeded';
    }
    if (error?.response?.status === 403) {
        return 'Meteoblue access forbidden';
    }
    const message = error?.response?.data?.error || error?.response?.data?.message;
    if (message) {
        return message;
    }
    if (error?.message) {
        return error.message;
    }
    return 'Failed to load weather data';
}

function processMeteoblueResponse(rawData, {requestedUnix}) {
    const hourly = rawData?.data_1h || {};
    const hourlyTimes = Array.isArray(hourly.time) ? hourly.time : [];
    if (!hourlyTimes.length) {
        throw new Error('Meteoblue hourly forecast data is missing');
    }

    const timestamps = hourlyTimes.map(value => toUnixFromIso(value));
    const startIndex = resolveStartIndex(timestamps, requestedUnix);

    const temperature = sliceAndRound(hourly.temperature, startIndex, 1);
    const humidity = sliceAndRound(hourly.relativehumidity, startIndex, 0);
    const windSpeed = sliceAndRound(hourly.windspeed, startIndex, 1);
    const windDirection = sliceAndRound(hourly.winddirection, startIndex, 0);
    const pressure = sliceAndRound(hourly.sealevelpressure, startIndex, 0);
    const precipitation = sliceAndRound(hourly.precipitation, startIndex, 2);
    const precipitationProbability = sliceAndRound(hourly.precipitation_probability, startIndex, 0);
    const feltTemperature = sliceAndRound(hourly.felttemperature, startIndex, 1);
    const surfWaveHeight = sliceAndRound(hourly.surfwave_height, startIndex, 2);
    const significantWaveHeight = sliceAndRound(hourly.significantwaveheight, startIndex, 2);
    const windWaveHeight = sliceAndRound(hourly.windwave_height, startIndex, 2);
    const seaSurfaceTemperature = sliceAndRound(hourly.seasurfacetemperature, startIndex, 1);
    const wavePeriod = sliceAndRound(hourly.windwave_meanperiod ?? hourly.mean_waveperiod, startIndex, 2);
    const waveDirection = sliceAndRound(hourly.mean_wavedirection ?? hourly.windwave_direction, startIndex, 0);
    const uvIndex = sliceAndRound(hourly.uvindex, startIndex, 0);

    const {windU, windV} = buildWindComponents(windSpeed, windDirection);
    const dewpoint = buildDewpointSeries(temperature, humidity);
    const rain3h = buildThreeHourAccumulation(precipitation);
    const clouds = buildCloudinessSeries(hourly, startIndex, humidity);
    const waveHeight = selectPreferredWaveHeight(significantWaveHeight, surfWaveHeight, windWaveHeight);

    const subsetTs = timestamps.slice(startIndex);

    const snowSeries = sliceAndRound(hourly.snowfall, startIndex, 2);

    return {
        ts: subsetTs,
        temp_2m: temperature,
        dewpoint_2m: dewpoint,
        rh_2m: humidity,
        wind_speed_10m: windSpeed,
        wind_u_10m: windU,
        wind_v_10m: windV,
        wind_direction_10m: windDirection,
        wind_gust: sliceAndRound(hourly.windgusts ?? hourly.windspeed, startIndex, 1),
        surface_pressure: pressure,
        rain_1h: precipitation,
        rain_3h: rain3h,
        snow_1h: snowSeries,
        snow_3h: buildThreeHourAccumulation(snowSeries),
        clouds,
        waves_height: waveHeight,
        waves_period: wavePeriod,
        waves_direction: waveDirection,
        felttemperature: feltTemperature,
        precipitation_probability: precipitationProbability,
        uv_index: uvIndex,
        sea_surface_temp: seaSurfaceTemperature,
        meta: {
            source: 'meteoblue',
            generationTime: rawData?.metadata?.modelrun_updatetime_utc,
            units: rawData?.units,
            current: normalizeCurrentWeather(rawData?.data_current),
            daily: extractDailySummaries(rawData?.data_day)
        }
    };
}

function toUnixFromIso(value) {
    if (!value) {
        return null;
    }
    if (typeof value === 'string' && value.includes('T')) {
        return dayjs.utc(value).unix();
    }
    return dayjs.utc(value, value.length === 10 ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm').unix();
}

function resolveStartIndex(timestamps, requestedUnix) {
    if (!timestamps.length) {
        return 0;
    }
    if (!requestedUnix) {
        return 0;
    }
    const target = Number(requestedUnix);
    if (!Number.isFinite(target)) {
        return 0;
    }
    const index = timestamps.findIndex(ts => ts !== null && ts >= target);
    if (index === -1) {
        return Math.max(timestamps.length - 1, 0);
    }
    return index;
}

function sliceAndRound(source, startIndex, decimals) {
    if (!Array.isArray(source)) {
        return [];
    }
    const factor = typeof decimals === 'number' ? Math.pow(10, decimals) : 1;
    return source.slice(startIndex).map(value => {
        const num = Number(value);
        if (!Number.isFinite(num)) {
            return null;
        }
        if (typeof decimals !== 'number') {
            return num;
        }
        return Math.round(num * factor) / factor;
    });
}

function buildWindComponents(speedSeries, directionSeries) {
    const length = Math.max(speedSeries.length, directionSeries.length);
    const windU = new Array(length).fill(null);
    const windV = new Array(length).fill(null);

    for (let i = 0; i < length; i += 1) {
        const speed = Number(speedSeries[i]);
        const direction = Number(directionSeries[i]);

        if (!Number.isFinite(speed) || !Number.isFinite(direction)) {
            continue;
        }

        const rad = direction * Math.PI / 180;
        const u = -speed * Math.sin(rad);
        const v = -speed * Math.cos(rad);
        windU[i] = Math.round(u * 100) / 100;
        windV[i] = Math.round(v * 100) / 100;
    }

    return {windU, windV};
}

function buildDewpointSeries(temperatureSeries, humiditySeries) {
    const length = Math.max(temperatureSeries.length, humiditySeries.length);
    const dewpoint = new Array(length).fill(null);

    for (let i = 0; i < length; i += 1) {
        const temp = Number(temperatureSeries[i]);
        const humidity = Number(humiditySeries[i]);

        if (!Number.isFinite(temp) || !Number.isFinite(humidity) || humidity <= 0 || humidity > 100) {
            continue;
        }

        const a = 17.27;
        const b = 237.7;
        const alpha = ((a * temp) / (b + temp)) + Math.log(humidity / 100);
        const dew = (b * alpha) / (a - alpha);
        dewpoint[i] = Math.round(dew * 10) / 10;
    }

    return dewpoint;
}

function buildThreeHourAccumulation(series) {
    if (!Array.isArray(series) || !series.length) {
        return [];
    }
    const result = [];
    for (let i = 0; i < series.length; i += 1) {
        const sum = [series[i], series[i + 1], series[i + 2]]
            .filter(value => Number.isFinite(Number(value)))
            .reduce((acc, value) => acc + Number(value), 0);
        result.push(Math.round(sum * 100) / 100);
    }
    return result;
}

function buildCloudinessSeries(hourly, startIndex, humiditySeries) {
    const candidateKeys = [
        'cloudcover',
        'cloudcover_total',
        'totalcloudcover',
        'cloudiness',
        'cloudcover_mean'
    ];

    for (const key of candidateKeys) {
        if (Array.isArray(hourly[key])) {
            return sliceAndRound(hourly[key], startIndex, 0);
        }
    }

    // Fallback: use relative humidity as loose proxy when dedicated cloud cover is unavailable.
    return [...humiditySeries];
}

function selectPreferredWaveHeight(significant, surf, wind) {
    if (significant.length) {
        return significant;
    }
    if (surf.length) {
        return surf;
    }
    return wind;
}

function normalizeCurrentWeather(data) {
    if (!data) {
        return null;
    }
    return {
        time: data.time ? toUnixFromIso(data.time) : null,
        temperature: Number.isFinite(Number(data.temperature)) ? Math.round(Number(data.temperature) * 10) / 10 : null,
        windspeed: Number.isFinite(Number(data.windspeed)) ? Math.round(Number(data.windspeed) * 10) / 10 : null,
        pictocode: data.pictocode,
        pictocodeDetailed: data.pictocode_detailed,
        humidity: Number.isFinite(Number(data.relativehumidity)) ? Math.round(Number(data.relativehumidity)) : null,
        isDaylight: data.isdaylight === 1
    };
}

function extractDailySummaries(dailyRaw) {
    const times = dailyRaw?.time;
    if (!Array.isArray(times) || !times.length) {
        return [];
    }

    const toSeries = (key, decimals) => sliceAndRound(dailyRaw[key], 0, decimals);
    const dateUnix = times.map(value => toUnixFromIso(value));
    const tempMean = toSeries('temperature_mean', 1);
    const tempInstant = toSeries('temperature_instant', 1);
    const windMean = toSeries('windspeed_mean', 1);
    const precipitation = toSeries('precipitation', 2);
    const humidityMean = toSeries('relativehumidity_mean', 0);
    const waveMax = toSeries('surfwave_height_max', 2);
    const predictability = toSeries('predictability', 0);
    const uvIndex = toSeries('uvindex', 0);
    const seaSurfaceTempMean = toSeries('seasurfacetemperature_mean', 1);
    const pressureMean = toSeries('sealevelpressure_mean', 0);

    return times.map((value, index) => ({
        date: dateUnix[index],
        label: value,
        temp_mean: tempMean[index] ?? null,
        temp_instant: tempInstant[index] ?? null,
        wind_avg: windMean[index] ?? null,
        rain_sum: precipitation[index] ?? null,
        humidity: humidityMean[index] ?? null,
        wave_max: waveMax[index] ?? null,
        sea_surface_temp: seaSurfaceTempMean[index] ?? null,
        predictability: predictability[index] ?? null,
        uv_index: uvIndex[index] ?? null,
        pressure: pressureMean[index] ?? null,
        precipitation_probability: Number.isFinite(Number(dailyRaw?.precipitation_probability?.[index]))
            ? Math.round(Number(dailyRaw.precipitation_probability[index]))
            : null
    }));
}

export function showInfo(coordinates) {
    let URL = 'api/v1/objectsinfo?lat=' + coordinates.lat + '&lon=' + coordinates.lng

    myAxios.get(URL).then(resp => {
        eventBus.$emit('show-map-info', {info: resp.data, coordinates: coordinates})
    })
}

export function deg2rad(deg) {
    return deg * (Math.PI / 180)
}

export function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
    let R = 6371;
    let dLat = deg2rad(lat2 - lat1);
    let dLon = deg2rad(lon2 - lon1);
    let a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2)
    ;
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    let d = R * c;
    return d;
}

